/**
 * StudioAdmin.jsx — Internal backend for the Founderboard Studios creative team.
 * Mounted at /studio/admin (gated by user.studio_admin === true; bounced to
 * /studio/login otherwise).  Lives behind the Studio brand, NOT the founder app.
 *
 * Sub-tabs (in order):
 *   1. Calendar — every deliverable across every customer (or just MINE for members)
 *   2. Orders   — order pipeline (existing)
 *   3. Customers — the people we're catering to, with their full profile + monthly updates
 *   4. Team     — manage Studio team members (Owners/Admins only)
 *
 * Roles:
 *   owner  → seeded (sid + lalith). Full rights. Can create admins + members.
 *   admin  → created by an owner. Can create members.  Cannot demote owners.
 *   member → assigned to specific customers; sees only their portfolio.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { formatDate } from '@/utils/datetime';
import { formatPhone } from '@/utils/phone';
import { useNavigate } from 'react-router-dom';
import {
  Plus, X, ArrowLeft, CalendarBlank, FileArrowUp, Trash, PencilSimple, Check, Spinner,
  CaretLeft, CaretRight, SignOut, MagnifyingGlass, UserCircle, Megaphone,
  Buildings, ShieldCheck, Crown, UserMinus, Phone, EnvelopeSimple,
} from '@phosphor-icons/react';
import { STUDIO_PLATFORMS, StudioPlatformIcon, getPlatformMeta } from '../components/StudioPlatformIcon';
import { StudioThemeProvider, StudioThemeToggle } from '../components/StudioTheme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ACCENT = '#6366f1';

const fmtINR = (n) => n == null ? '—' : n >= 100000 ? `₹${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)}L` : n >= 1000 ? `₹${(n / 1000).toFixed(0)}K` : `₹${n}`;

// ---------- Top-level layout ----------
const StudioAdmin = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('calendar');

  const role = user?.studio_role || (user?.is_super_admin ? 'owner' : 'member');
  const canManageTeam = role === 'owner' || role === 'admin';

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser?.(null);
    navigate('/studio/login', { replace: true });
  };

  const TABS = [
    { id: 'calendar', label: 'Calendar', icon: CalendarBlank },
    { id: 'orders', label: 'Orders', icon: Megaphone },
    { id: 'customers', label: 'Customers', icon: Buildings },
    ...(canManageTeam ? [{ id: 'team', label: 'Team', icon: UserCircle }] : []),
  ];

  return (
    <StudioThemeProvider className="min-h-screen bg-[var(--s-bg)] text-[var(--s-text)]">
      <div data-testid="studio-admin-page">
      {/* Header */}
      <header className="border-b border-[var(--s-border)] px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-30 bg-[var(--s-bg)]/95 backdrop-blur">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}aa)` }}
          >
            <Megaphone size={16} weight="fill" />
          </span>
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.24em] font-bold" style={{ color: ACCENT }}>
              Studios · Admin
            </div>
            <div className="text-sm font-heading font-bold leading-tight truncate" style={{ letterSpacing: '-0.01em' }}>
              {user?.full_name || user?.email}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StudioThemeToggle />
          <span
            className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border border-[var(--s-border)]"
            style={{ color: ACCENT }}
            data-testid="studio-role-badge"
          >
            {role === 'owner' ? <Crown size={11} weight="fill" /> : role === 'admin' ? <ShieldCheck size={11} weight="fill" /> : <UserCircle size={11} weight="fill" />}
            {role}
          </span>
          <button
            onClick={logout}
            data-testid="studio-logout"
            className="inline-flex items-center gap-1.5 text-xs text-danger hover:text-danger/80 px-3 py-1.5 rounded border border-[var(--s-border)] hover:border-danger/40 transition-colors"
          >
            <SignOut size={12} weight="bold" /> Sign out
          </button>
        </div>
      </header>

      {/* Tab nav */}
      <div className="border-b border-[var(--s-border)] px-4 sm:px-6 bg-[var(--s-bg)]">
        <div className="flex items-center gap-1 overflow-x-auto" data-testid="studio-admin-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`studio-admin-tab-${t.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-3 text-[12px] font-medium border-b-2 transition-colors whitespace-nowrap ${
                  active ? 'text-[var(--s-text)]' : 'text-[var(--s-text-dim)] hover:text-[var(--s-text)] border-transparent'
                }`}
                style={active ? { borderColor: ACCENT, color: ACCENT } : {}}
              >
                <Icon size={13} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <main className="px-4 sm:px-6 py-6 max-w-[1400px] mx-auto">
        {tab === 'calendar' && <AdminCalendar role={role} />}
        {tab === 'orders' && <AdminOrdersTab />}
        {tab === 'customers' && <AdminCustomersTab role={role} />}
        {tab === 'team' && canManageTeam && <AdminTeamTab currentUser={user} myRole={role} />}
      </main>
      </div>
    </StudioThemeProvider>
  );
};

// ============================================================
//  CALENDAR (default tab) — unified across customers
// ============================================================
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const buildCells = (year, monthIdx) => {
  const first = new Date(year, monthIdx, 1);
  const firstDow = (first.getDay() + 6) % 7;
  const days = new Date(year, monthIdx + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push({ key: `pre-${i}`, date: null });
  for (let d = 1; d <= days; d++) {
    const iso = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ key: iso, date: iso, day: d });
  }
  while (cells.length % 7 !== 0) cells.push({ key: `post-${cells.length}`, date: null });
  return cells;
};

const AdminCalendar = ({ role }) => {
  const today = new Date();
  const [cursor, setCursor] = useState({ year: today.getFullYear(), monthIdx: today.getMonth() });
  const [scope, setScope] = useState(role === 'member' ? 'mine' : 'all');
  const [data, setData] = useState({ deliverables: [], stats: { total: 0, this_month: 0, pending_review: 0, by_platform: {} }, scope: 'mine' });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/studio/admin/calendar?scope=${scope}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load calendar');
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [scope]);

  const byDate = useMemo(() => {
    const m = {};
    for (const d of data.deliverables) {
      const k = (d.delivery_date || '').slice(0, 10);
      if (!k) continue; (m[k] ||= []).push(d);
    }
    return m;
  }, [data]);

  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const cells = buildCells(cursor.year, cursor.monthIdx);
  const goPrev = () => setCursor(({ year, monthIdx }) => monthIdx === 0 ? { year: year - 1, monthIdx: 11 } : { year, monthIdx: monthIdx - 1 });
  const goNext = () => setCursor(({ year, monthIdx }) => monthIdx === 11 ? { year: year + 1, monthIdx: 0 } : { year, monthIdx: monthIdx + 1 });
  const goToday = () => setCursor({ year: today.getFullYear(), monthIdx: today.getMonth() });

  const topPlats = Object.entries(data.stats.by_platform || {}).sort(([, a], [, b]) => b - a).slice(0, 4);

  return (
    <div data-testid="studio-admin-calendar">
      {/* scope + stats row */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 flex-1">
          <StatBlock label="This month" value={data.stats.this_month} />
          <StatBlock label="Pending review" value={data.stats.pending_review} />
          <StatBlock label="Total" value={data.stats.total} />
          <div className="border-l-2 pl-4 py-1" style={{ borderLeftColor: ACCENT }}>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-dim)] mb-1">By platform</div>
            <div className="flex items-center gap-2 flex-wrap min-h-[28px]">
              {topPlats.length === 0 ? <span className="text-sm text-[var(--s-text-faint)]">—</span> : topPlats.map(([pid, count]) => {
                const meta = getPlatformMeta(pid);
                return (
                  <span key={pid} className="inline-flex items-center gap-1.5 text-xs text-[var(--s-text-muted)]" title={meta.label}>
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: meta.color }} />
                    <span className="font-mono font-semibold text-[var(--s-text)]">{count}</span>
                    <span className="hidden lg:inline whitespace-nowrap">{meta.label}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>
        {role !== 'member' && (
          <div className="inline-flex items-center gap-1 p-1 rounded-lg border border-[var(--s-border)] self-start" data-testid="studio-admin-calendar-scope">
            {[{ id: 'mine', label: 'My clients' }, { id: 'all', label: 'All clients' }].map((s) => {
              const a = scope === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setScope(s.id)}
                  data-testid={`studio-admin-calendar-scope-${s.id}`}
                  className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-all ${a ? '' : 'text-[var(--s-text-dim)] hover:text-[var(--s-text)]'}`}
                  style={a ? { backgroundColor: `${ACCENT}26`, color: ACCENT } : {}}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <button onClick={goPrev} data-testid="studio-admin-cal-prev" className="w-8 h-8 rounded-md border border-[var(--s-border)] hover:border-[var(--s-border-strong)] flex items-center justify-center text-[var(--s-text-muted)] hover:text-[var(--s-text)] transition-colors" aria-label="Previous month">
            <CaretLeft size={13} weight="bold" />
          </button>
          <h2 className="font-heading font-bold text-[var(--s-text)] text-lg sm:text-xl" style={{ letterSpacing: '-0.01em' }} data-testid="studio-admin-cal-title">
            {MONTH_NAMES[cursor.monthIdx]} {cursor.year}
          </h2>
          <button onClick={goNext} data-testid="studio-admin-cal-next" className="w-8 h-8 rounded-md border border-[var(--s-border)] hover:border-[var(--s-border-strong)] flex items-center justify-center text-[var(--s-text-muted)] hover:text-[var(--s-text)] transition-colors" aria-label="Next month">
            <CaretRight size={13} weight="bold" />
          </button>
        </div>
        <button onClick={goToday} data-testid="studio-admin-cal-today" className="text-[11px] uppercase tracking-wider font-bold border border-[var(--s-border)] rounded-md px-3 py-1.5 text-[var(--s-text-muted)] hover:text-[var(--s-text)] hover:border-[var(--s-border-strong)] transition-colors">
          Today
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-[var(--s-text-dim)]"><Spinner className="animate-spin inline mr-2" size={14} /> Loading…</div>
      ) : data.deliverables.length === 0 ? (
        <div className="border border-dashed border-[var(--s-border)] rounded-xl py-16 text-center" data-testid="studio-admin-calendar-empty">
          <CalendarBlank size={28} className="mx-auto mb-3 text-[var(--s-text-faint)]" />
          <h3 className="text-lg font-heading font-bold text-[var(--s-text)] mb-1">No deliverables yet</h3>
          <p className="text-sm text-[var(--s-text-dim)]">
            {scope === 'mine' ? "You haven't been assigned any customers yet, or none of them have deliverables." : "Push your first deliverable from the Orders tab."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-7 border border-[var(--s-border)] rounded-lg overflow-hidden bg-[var(--s-bg)]" data-testid="studio-admin-cal-grid">
          {DAY_HEADERS.map((d) => (
            <div key={d} className="text-[10px] uppercase tracking-wider font-bold text-[var(--s-text-dim)] text-center py-2 border-b border-r border-[var(--s-border)] last:border-r-0 bg-[var(--s-surface-2)]">{d}</div>
          ))}
          {cells.map((c, idx) => {
            if (!c.date) return <div key={c.key} className={`min-h-[120px] bg-[var(--s-surface-2)]/40 border-r border-b border-[var(--s-border)] ${(idx + 1) % 7 === 0 ? 'border-r-0' : ''}`} />;
            const isToday = c.date === todayIso;
            const dayDelivs = byDate[c.date] || [];
            const visible = dayDelivs.slice(0, 3);
            const overflow = dayDelivs.length - visible.length;
            return (
              <div
                key={c.key}
                data-testid={`studio-admin-cal-cell-${c.date}`}
                className={`min-h-[120px] p-2 border-r border-b border-[var(--s-border)] flex flex-col gap-1.5 transition-colors ${(idx + 1) % 7 === 0 ? 'border-r-0' : ''} ${isToday ? 'bg-[var(--s-surface-2)]' : 'hover:bg-[var(--s-surface-2)]/60'}`}
              >
                <div className="flex items-center justify-end mb-0.5">
                  {isToday ? (
                    <span className="text-[11px] font-bold w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: ACCENT, color: 'white' }}>{c.day}</span>
                  ) : (
                    <span className="text-[11px] font-medium text-[var(--s-text-muted)] px-1">{c.day}</span>
                  )}
                </div>
                <div className="flex flex-col gap-1 overflow-hidden">
                  {visible.map((d) => {
                    const meta = getPlatformMeta(d.platform);
                    return (
                      <button
                        key={d.id}
                        onClick={() => setOpen(d.id)}
                        data-testid={`studio-admin-deliverable-${d.id}`}
                        title={`${meta.label} · ${d.title} · ${d.customer_name || ''}`}
                        className="flex items-center gap-1.5 w-full px-1.5 py-1 rounded-md border border-[var(--s-border)] bg-[var(--s-surface-2)] hover:bg-[var(--s-surface-hover)] hover:border-[var(--s-border-strong)] hover:-translate-y-[1px] transition-all text-left group"
                      >
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: meta.color }} />
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-medium text-[var(--s-text)] truncate">{d.title}</div>
                          <div className="text-[9px] text-[var(--s-text-dim)] truncate">{d.customer_name}</div>
                        </div>
                      </button>
                    );
                  })}
                  {overflow > 0 && (
                    <button onClick={() => setOpen(dayDelivs[3].id)} className="text-[10px] text-[var(--s-text-dim)] hover:text-[var(--s-text)] text-left px-1 transition-colors">+{overflow} more</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && <AdminDeliverableModal deliv={data.deliverables.find(x => x.id === open)} onClose={() => setOpen(null)} />}
    </div>
  );
};

const StatBlock = ({ label, value }) => (
  <div className="border-l-2 pl-4 py-1" style={{ borderLeftColor: ACCENT }}>
    <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-dim)] mb-1">{label}</div>
    <div className="text-2xl font-heading font-bold text-[var(--s-text)] leading-tight" style={{ letterSpacing: '-0.02em' }}>{value}</div>
  </div>
);

// ============================================================
//  ORDERS TAB
// ============================================================
const AdminOrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/studio/admin/orders${statusFilter ? `?status=${statusFilter}` : ''}`);
      setOrders(r.data.orders || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [statusFilter]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-bold text-[var(--s-text)] text-lg" style={{ letterSpacing: '-0.01em' }}>Order pipeline</h3>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="studio-admin-status-filter"
            className="bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-md px-2 py-1 text-xs text-[var(--s-text)]"
          >
            <option value="">All</option>
            <option value="pending_payment">Pending payment</option>
            <option value="in_production">In production</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        {loading ? <div className="text-sm text-[var(--s-text-dim)]">Loading…</div> : orders.length === 0 ? (
          <div className="text-sm text-[var(--s-text-dim)] border border-[var(--s-border)] rounded-lg p-6 text-center">No orders match this filter.</div>
        ) : orders.map((o) => (
          <button
            key={o.id}
            onClick={() => setActiveId(o.id)}
            data-testid={`studio-admin-order-${o.id}`}
            className={`w-full text-left bg-[var(--s-surface-2)] border rounded-lg p-3 transition-colors ${activeId === o.id ? 'border-white' : 'border-[var(--s-border)] hover:border-[var(--s-border-strong)]'}`}
            style={activeId === o.id ? { borderColor: ACCENT } : {}}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: ACCENT }}>{o.status?.replace('_', ' ')}</span>
              <span className="text-[10px] text-[var(--s-text-dim)]">{formatDate(o.created_at)}</span>
            </div>
            <div className="text-sm font-medium text-[var(--s-text)]">{o.user_full_name || o.user_email}</div>
            <div className="text-[11px] text-[var(--s-text-muted)]">{o.items.length} item(s) · {fmtINR(o.total)}</div>
          </button>
        ))}
      </div>
      <div>
        {activeId ? <AdminOrderDetail orderId={activeId} onReload={load} /> : (
          <div className="border border-[var(--s-border)] rounded-xl p-12 text-center text-sm text-[var(--s-text-dim)]">← Pick an order to manage its deliverables.</div>
        )}
      </div>
    </div>
  );
};

const AdminOrderDetail = ({ orderId, onReload }) => {
  const [order, setOrder] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/studio/orders/${orderId}`);
      setOrder(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [orderId]);

  const updateStatus = async (s) => {
    try {
      await api.patch(`/studio/admin/orders/${orderId}`, { status: s });
      toast.success('Status updated');
      await load(); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!order) return <div className="text-sm text-[var(--s-text-dim)] p-6">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="studio-admin-order-detail">
      <div className="border border-[var(--s-border)] rounded-xl p-5">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: ACCENT }}>Order {order.id.slice(0, 8)}</div>
            <h3 className="font-heading font-bold text-[var(--s-text)] text-lg">{order.user_full_name}</h3>
            <div className="text-[12px] text-[var(--s-text-muted)]">{order.user_email}</div>
          </div>
          <select value={order.status} onChange={(e) => updateStatus(e.target.value)} data-testid="studio-admin-status-update" className="bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-md px-2 py-1 text-xs text-[var(--s-text)]">
            <option value="pending_payment">Pending payment</option>
            <option value="in_production">In production</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="border border-[var(--s-border)] rounded-lg p-3 space-y-1 mb-1">
          {order.items.map((i) => (
            <div key={i.line_id} className="flex justify-between text-[12px]">
              <span className="text-[var(--s-text)] truncate pr-2">{i.name}</span>
              <span className="text-[var(--s-text-muted)] font-mono whitespace-nowrap">{fmtINR(i.price)} {i.cadence === 'monthly' ? '/mo' : ''}</span>
            </div>
          ))}
          <div className="border-t border-[var(--s-border)] mt-2 pt-2 flex justify-between text-sm font-semibold">
            <span>Total</span><span className="font-mono">{fmtINR(order.total)}</span>
          </div>
        </div>
      </div>

      <div className="border border-[var(--s-border)] rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading font-bold text-[var(--s-text)] text-base">Deliverables on this order</h4>
          <button onClick={() => setShowAdd(true)} data-testid="studio-admin-add-deliv-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white" style={{ backgroundColor: ACCENT }}>
            <Plus size={11} weight="bold" /> Add deliverable
          </button>
        </div>
        {(!order.deliverables || order.deliverables.length === 0) ? (
          <div className="text-center py-6 text-sm text-[var(--s-text-dim)]">
            <CalendarBlank size={22} className="text-[var(--s-text-faint)] mx-auto mb-1" /> No deliverables yet.
          </div>
        ) : (
          <div className="space-y-2">
            {order.deliverables.map((d) => (
              <div key={d.id} className="border border-[var(--s-border)] rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`studio-admin-deliv-${d.id}`}>
                <StudioPlatformIcon platform={d.platform} size={28} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--s-text-dim)] flex items-center gap-1.5">
                    <span>{d.delivery_date}</span>
                    <span className="text-[var(--s-text-faint)]">·</span>
                    <span style={{ color: getPlatformMeta(d.platform).color }} className="font-semibold normal-case tracking-normal">{getPlatformMeta(d.platform).label}</span>
                  </div>
                  <div className="text-sm font-medium text-[var(--s-text)] truncate">{d.title}</div>
                  {d.purpose && <div className="text-[11px] text-[var(--s-text-muted)] line-clamp-1 italic">{d.purpose}</div>}
                </div>
                {d.file_id && <a href={`${API}/files/${d.file_id}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`} target="_blank" rel="noreferrer" className="text-[var(--s-text)] text-[11px] underline">Open</a>}
                <button onClick={async () => { if (!window.confirm('Delete this deliverable?')) return; try { await api.delete(`/studio/admin/deliverables/${d.id}`); toast.success('Deleted'); await load(); } catch { toast.error('Failed'); } }} data-testid={`studio-admin-deliv-delete-${d.id}`} className="text-[var(--s-text-dim)] hover:text-red-400">
                  <Trash size={13} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && <AddDeliverableModal orderId={orderId} onClose={() => setShowAdd(false)} onSaved={async () => { setShowAdd(false); await load(); }} />}
    </div>
  );
};

// ============================================================
//  ADD DELIVERABLE MODAL  (re-used)
// ============================================================
const AddDeliverableModal = ({ orderId, onClose, onSaved }) => {
  const [title, setTitle] = useState('');
  const [platform, setPlatform] = useState('linkedin_post');
  const [purpose, setPurpose] = useState('');
  const [desc, setDesc] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [fileId, setFileId] = useState(null);
  const [filename, setFilename] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/files/upload', fd);
      setFileId(r.data.file_id); setFilename(r.data.filename);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      await api.post('/studio/admin/deliverables', { order_id: orderId, title, description: desc, delivery_date: date, file_id: fileId, platform, purpose });
      toast.success('Deliverable added — founder notified.');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur z-50 flex items-center justify-center p-4" data-testid="studio-admin-add-deliv-modal">
      <div className="bg-[var(--s-bg)] border border-[var(--s-border)] rounded-xl p-6 w-[620px] max-w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-[var(--s-text)] text-lg">New deliverable</h3>
          <button onClick={onClose} className="text-[var(--s-text-dim)] hover:text-[var(--s-text)]"><X size={18} weight="bold" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-[var(--s-text-dim)] mb-2">Where will this go?</label>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="studio-admin-deliv-platform-grid">
              {STUDIO_PLATFORMS.map((p) => {
                const selected = platform === p.id;
                return (
                  <button key={p.id} type="button" onClick={() => setPlatform(p.id)} data-testid={`studio-admin-deliv-platform-${p.id}`} className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border text-center transition-all ${selected ? '' : 'border-[var(--s-border)] hover:border-[var(--s-border-strong)]'}`} style={selected ? { borderColor: p.color, backgroundColor: `${p.color}1a` } : {}}>
                    <StudioPlatformIcon platform={p.id} size={26} />
                    <span className="text-[10px] leading-tight text-[var(--s-text-muted)] line-clamp-2 h-[24px]">{p.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-[var(--s-text-dim)] mb-1.5">Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`e.g. ${getPlatformMeta(platform).label} — Founder origin story`} data-testid="studio-admin-deliv-title" className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--s-border-strong)] placeholder:text-[var(--s-text-faint)]" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-[var(--s-text-dim)] mb-1.5">Why we created this <span className="text-[var(--s-text-faint)] normal-case font-normal">(summary / strategic intent)</span></label>
            <textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={2} placeholder="e.g. Builds founder credibility with early-stage angels…" data-testid="studio-admin-deliv-purpose" className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--s-border-strong)] placeholder:text-[var(--s-text-faint)]" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-[var(--s-text-dim)] mb-1.5">Caption / Talking points <span className="text-[var(--s-text-faint)] normal-case font-normal">(optional)</span></label>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} placeholder="The actual copy, caption, or speaker notes." data-testid="studio-admin-deliv-desc" className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--s-border-strong)] placeholder:text-[var(--s-text-faint)]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider font-bold text-[var(--s-text-dim)] mb-1.5">Delivery date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="studio-admin-deliv-date" className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--s-border-strong)]" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider font-bold text-[var(--s-text-dim)] mb-1.5">Attachment <span className="text-[var(--s-text-faint)] normal-case font-normal">(optional)</span></label>
              <label className="flex items-center justify-between gap-2 px-3 py-2 bg-[var(--s-surface-2)] border border-dashed border-[var(--s-border)] rounded-lg hover:border-[var(--s-border-strong)] cursor-pointer h-[38px]">
                <span className="text-[12px] text-[var(--s-text-muted)] flex-1 truncate">{filename ? <><Check size={11} weight="fill" className="inline text-green-400 mr-1" /> {filename}</> : (uploading ? 'Uploading…' : 'Click to attach')}</span>
                <FileArrowUp size={14} weight="bold" style={{ color: ACCENT }} />
                <input type="file" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid="studio-admin-deliv-file" />
              </label>
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-[var(--s-border)] rounded-lg text-[var(--s-text-muted)] hover:text-[var(--s-text)]">Cancel</button>
          <button onClick={save} disabled={saving || uploading} data-testid="studio-admin-deliv-save" className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: ACCENT }}>
            {saving ? <Spinner size={14} className="animate-spin" /> : <Check size={14} weight="bold" />} Add to calendar
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
//  DELIVERABLE READ-ONLY MODAL  (calendar pill click)
// ============================================================
const AdminDeliverableModal = ({ deliv, onClose }) => {
  if (!deliv) return null;
  const meta = getPlatformMeta(deliv.platform);
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-xl z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="studio-admin-deliv-modal">
      <div className="w-full max-w-2xl bg-[var(--s-bg)] border border-[var(--s-border)] rounded-xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold text-white" style={{ backgroundColor: meta.color }}>
              <StudioPlatformIcon platform={deliv.platform} size={14} rounded={3} className="!bg-transparent" />
              {meta.label}
            </span>
            <span className="text-[11px] text-[var(--s-text-dim)] font-mono">{deliv.delivery_date}</span>
            <span className="text-[11px] text-[var(--s-text-muted)] truncate">· {deliv.customer_name}</span>
          </div>
          <button onClick={onClose} className="text-[var(--s-text-dim)] hover:text-[var(--s-text)] flex-shrink-0"><X size={18} weight="bold" /></button>
        </div>
        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-[var(--s-text)] leading-tight mb-5" style={{ letterSpacing: '-0.02em' }}>{deliv.title}</h2>
        {deliv.purpose && (
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-dim)] mb-2">Why we made this</div>
            <p className="text-sm text-[var(--s-text-muted)] whitespace-pre-wrap leading-relaxed">{deliv.purpose}</p>
          </div>
        )}
        {deliv.description && (
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-dim)] mb-2">Caption / talking points</div>
            <blockquote className="border-l-2 border-white/20 pl-4 text-sm text-[var(--s-text-muted)] whitespace-pre-wrap leading-relaxed">{deliv.description}</blockquote>
          </div>
        )}
        {deliv.order_item_label && (
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-dim)] mb-2">From order</div>
            <p className="text-sm text-[var(--s-text-muted)]">{deliv.order_item_label}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
//  CUSTOMERS TAB
// ============================================================
const AdminCustomersTab = ({ role }) => {
  const [list, setList] = useState([]);
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [active, setActive] = useState(null);  // {type, id}
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (kind) params.set('kind', kind);
      if (q) params.set('q', q);
      const r = await api.get(`/studio/admin/customers?${params}`);
      setList(r.data.customers || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [kind, q]);

  useEffect(() => {
    if (!active) { setDetail(null); return; }
    (async () => {
      try {
        const r = await api.get(`/studio/admin/customers/${active.type}/${active.id}`);
        setDetail(r.data);
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load customer'); setDetail(null); }
    })();
    // eslint-disable-next-line
  }, [active]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6" data-testid="studio-admin-customers">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--s-text-faint)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" data-testid="studio-admin-cust-search" className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-md pl-9 pr-3 py-2 text-sm text-[var(--s-text)] placeholder:text-[var(--s-text-faint)] focus:outline-none focus:border-[var(--s-border-strong)]" />
          </div>
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="studio-admin-cust-kind" className="bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-md px-2 py-2 text-xs text-[var(--s-text)]">
            <option value="">All</option>
            <option value="startup">Startups</option>
            <option value="vc">VCs</option>
            <option value="incubator">Incubators</option>
          </select>
        </div>
        <div className="space-y-1.5 max-h-[70vh] overflow-y-auto">
          {loading ? <div className="text-sm text-[var(--s-text-dim)] p-4">Loading…</div> : list.length === 0 ? (
            <div className="text-sm text-[var(--s-text-dim)] border border-[var(--s-border)] rounded-lg p-6 text-center">
              {role === 'member' ? 'No customers assigned to you yet — ask an owner to assign you.' : 'No customers match.'}
            </div>
          ) : list.map((c) => {
            const isActive = active?.type === c.type && active?.id === c.id;
            return (
              <button key={`${c.type}-${c.id}`} onClick={() => setActive({ type: c.type, id: c.id })} data-testid={`studio-admin-cust-${c.id}`} className={`w-full text-left bg-[var(--s-surface-2)] border rounded-lg p-3 transition-colors ${isActive ? '' : 'border-[var(--s-border)] hover:border-[var(--s-border-strong)]'}`} style={isActive ? { borderColor: ACCENT } : {}}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-[var(--s-text)] truncate">{c.name || '—'}</span>
                  <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ color: ACCENT, backgroundColor: `${ACCENT}1a` }}>{c.type}</span>
                </div>
                <div className="text-[11px] text-[var(--s-text-dim)] mt-0.5 truncate">{c.sector || c.stage_focus || c.founder_email || ''}</div>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        {!active ? (
          <div className="border border-[var(--s-border)] rounded-xl p-12 text-center text-sm text-[var(--s-text-dim)]" data-testid="studio-admin-cust-empty">
            ← Pick a customer to see their profile, team, and monthly updates.
          </div>
        ) : !detail ? (
          <div className="text-sm text-[var(--s-text-dim)] p-6"><Spinner size={14} className="animate-spin inline mr-2" /> Loading…</div>
        ) : (
          <CustomerDetail detail={detail} />
        )}
      </div>
    </div>
  );
};

const CustomerDetail = ({ detail }) => {
  const p = detail.profile || {};
  const isStartup = detail.type === 'startup';
  const displayName = p.startup_name || p.name || p.fund_name || '—';
  const phone = p.founder_phone || p.phone || '';
  const email = p.founder_email || (detail.team[0]?.email) || '';
  return (
    <div className="space-y-5" data-testid={`studio-admin-cust-detail-${detail.id}`}>
      {/* Hero */}
      <div className="border border-[var(--s-border)] rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: ACCENT }}>{detail.type}</div>
            <h2 className="text-2xl font-heading font-bold text-[var(--s-text)] leading-tight" style={{ letterSpacing: '-0.02em' }}>{displayName}</h2>
            {p.sector && <div className="text-sm text-[var(--s-text-muted)] mt-0.5">{p.sector}{p.stage ? ` · ${p.stage}` : ''}</div>}
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-[var(--s-text)]">{detail.deliverables_count}</div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--s-text-dim)]">deliverables</div>
            <div className="text-[10px] text-[var(--s-text-dim)] mt-1">{detail.orders_count} order{detail.orders_count === 1 ? '' : 's'}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
          {email && <a href={`mailto:${email}`} className="inline-flex items-center gap-1.5 text-[var(--s-text)]/80 hover:text-[var(--s-text)]"><EnvelopeSimple size={13} weight="bold" /> {email}</a>}
          {phone && <a href={`tel:${phone}`} className="inline-flex items-center gap-1.5 text-[var(--s-text)]/80 hover:text-[var(--s-text)]"><Phone size={13} weight="bold" /> {formatPhone(phone)}</a>}
        </div>
        {p.tagline && <p className="text-sm text-[var(--s-text-muted)] mt-4 italic">"{p.tagline}"</p>}
      </div>

      {/* Team */}
      {detail.team?.length > 0 && (
        <div className="border border-[var(--s-border)] rounded-xl p-5">
          <h3 className="text-[11px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-dim)] mb-3">Team ({detail.team.length})</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {detail.team.map((t) => (
              <div key={t.id} className="border border-[var(--s-border)] rounded-lg p-3" data-testid={`studio-admin-cust-team-${t.id}`}>
                <div className="text-sm font-medium text-[var(--s-text)]">{t.full_name || t.email}</div>
                <div className="text-[11px] text-[var(--s-text-dim)]">{t.email}</div>
                {t.phone && <div className="text-[11px] text-[var(--s-text-dim)] mt-0.5">{formatPhone(t.phone)}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly updates (startups only) */}
      {isStartup && detail.monthly_updates?.length > 0 && (
        <div className="border border-[var(--s-border)] rounded-xl p-5">
          <h3 className="text-[11px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-dim)] mb-3">Recent monthly updates</h3>
          <div className="space-y-3">
            {detail.monthly_updates.map((u, i) => (
              <div key={i} className="border border-[var(--s-border)] rounded-lg p-3" data-testid={`studio-admin-cust-update-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-[var(--s-text)]">{MONTH_NAMES[(u.month || 1) - 1]} {u.year}</span>
                  <div className="flex items-center gap-3 text-[11px] text-[var(--s-text-muted)]">
                    {u.mrr != null && <span><span className="text-[var(--s-text-dim)]">MRR</span> <span className="font-mono text-[var(--s-text)]">{fmtINR(u.mrr)}</span></span>}
                    {u.runway_months != null && <span><span className="text-[var(--s-text-dim)]">Runway</span> <span className="font-mono text-[var(--s-text)]">{u.runway_months}mo</span></span>}
                  </div>
                </div>
                {u.wins && <div className="text-[12px] text-[var(--s-text-muted)] line-clamp-2 mb-1"><span className="font-bold text-green-400">Wins:</span> {u.wins}</div>}
                {u.challenges && <div className="text-[12px] text-[var(--s-text-muted)] line-clamp-2 mb-1"><span className="font-bold text-amber-400">Challenges:</span> {u.challenges}</div>}
                {u.goals && <div className="text-[12px] text-[var(--s-text-muted)] line-clamp-2"><span className="font-bold" style={{ color: ACCENT }}>Goals:</span> {u.goals}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
//  TEAM TAB — owners + admins manage Studio teammates
// ============================================================
const AdminTeamTab = ({ currentUser, myRole }) => {
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, i] = await Promise.all([
        api.get('/studio/admin/team'),
        api.get('/studio/admin/invitations'),
      ]);
      setMembers(m.data.members || []);
      setInvitations(i.data.invitations || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, []);

  const deactivate = async (m) => {
    if (!window.confirm(`Deactivate ${m.full_name || m.email}?`)) return;
    try {
      await api.delete(`/studio/admin/team/${m.id}`);
      toast.success('Deactivated');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const revokeInvite = async (inv) => {
    if (!window.confirm(`Revoke invite to ${inv.email}?`)) return;
    try {
      await api.delete(`/studio/admin/invitations/${inv.id}`);
      toast.success('Invite revoked');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="studio-admin-team">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-2xl font-heading font-bold text-[var(--s-text)]" style={{ letterSpacing: '-0.02em' }}>Studio team</h2>
          <p className="text-sm text-[var(--s-text-muted)] mt-1">{members.length} member{members.length === 1 ? '' : 's'}{invitations.length ? ` · ${invitations.length} pending invite${invitations.length === 1 ? '' : 's'}` : ''} · {myRole === 'owner' ? 'You can promote, demote, and deactivate' : 'You can invite new members'}</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="studio-admin-team-add-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-white" style={{ backgroundColor: ACCENT }}>
          <Plus size={13} weight="bold" /> Invite teammate
        </button>
      </div>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <div className="border border-[var(--s-border)] rounded-xl bg-[var(--s-bg)] mb-5 overflow-hidden" data-testid="studio-admin-team-pending">
          <div className="px-4 py-2.5 border-b border-[var(--s-border)] bg-[var(--s-border)] flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-muted)]">Pending invitations ({invitations.length})</span>
          </div>
          <div className="divide-y divide-[var(--s-border)]">
            {invitations.map((inv) => (
              <div key={inv.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`studio-admin-invite-${inv.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[var(--s-text)] truncate">{inv.email}</span>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${ACCENT}1a`, color: ACCENT }}>
                      {inv.studio_role === 'admin' ? <ShieldCheck size={9} weight="fill" /> : <UserCircle size={9} weight="fill" />}
                      {inv.studio_role}
                    </span>
                    <span className="text-[10px] text-[var(--s-text-dim)]">· {inv.assigned_customers?.length || 0} customer{(inv.assigned_customers?.length || 0) === 1 ? '' : 's'}</span>
                  </div>
                  <div className="text-[11px] text-[var(--s-text-dim)]">Sent by {inv.invited_by_name} · expires {inv.expires_at ? formatDate(inv.expires_at) : '—'}</div>
                </div>
                <button onClick={() => revokeInvite(inv)} data-testid={`studio-admin-invite-revoke-${inv.id}`} className="px-2 py-1 text-[11px] text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-500 rounded-md">
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? <div className="text-sm text-[var(--s-text-dim)]">Loading…</div> : (
        <div className="border border-[var(--s-border)] rounded-xl divide-y divide-white/10 overflow-hidden">
          {members.map((m) => {
            const role = m.studio_role || 'member';
            const isOwner = role === 'owner';
            const isMe = m.id === currentUser?.id;
            const inactive = m.studio_active === false;
            return (
              <div key={m.id} className={`px-5 py-4 flex flex-col md:flex-row md:items-center justify-between gap-3 ${inactive ? 'opacity-50' : ''}`} data-testid={`studio-admin-team-row-${m.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[var(--s-text)] truncate">{m.full_name || m.email}</span>
                    {isMe && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-white/10 text-[var(--s-text-muted)]">You</span>}
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${ACCENT}1a`, color: ACCENT }}>
                      {isOwner ? <Crown size={9} weight="fill" /> : role === 'admin' ? <ShieldCheck size={9} weight="fill" /> : <UserCircle size={9} weight="fill" />}
                      {role}
                    </span>
                    {inactive && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">deactivated</span>}
                  </div>
                  <div className="text-[12px] text-[var(--s-text-dim)]">{m.email}</div>
                  {!!(m.studio_assigned_customers && m.studio_assigned_customers.length) && (
                    <div className="text-[11px] text-[var(--s-text-dim)] mt-1 truncate">
                      Catering to: {m.studio_assigned_customers.map((a) => a.name).join(' · ')}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!isOwner && (
                    <button onClick={() => setEditing(m)} data-testid={`studio-admin-team-edit-${m.id}`} className="px-2 py-1 text-[11px] text-[var(--s-text-muted)] hover:text-[var(--s-text)] border border-[var(--s-border)] hover:border-[var(--s-border-strong)] rounded-md inline-flex items-center gap-1">
                      <PencilSimple size={11} weight="bold" /> Edit
                    </button>
                  )}
                  {myRole === 'owner' && !isOwner && !inactive && (
                    <button onClick={() => deactivate(m)} data-testid={`studio-admin-team-deactivate-${m.id}`} className="px-2 py-1 text-[11px] text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-500 rounded-md inline-flex items-center gap-1">
                      <UserMinus size={11} weight="bold" /> Deactivate
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <TeamMemberModal myRole={myRole} onClose={() => setShowAdd(false)} onSaved={async () => { setShowAdd(false); await load(); }} />}
      {editing && <TeamMemberModal myRole={myRole} member={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
    </div>
  );
};

const TeamMemberModal = ({ member, myRole, onClose, onSaved }) => {
  const isEdit = !!member;
  const [email, setEmail] = useState(member?.email || '');
  const [fullName, setFullName] = useState(member?.full_name || '');
  const [role, setRole] = useState(member?.studio_role || 'member');
  const [assignedIds, setAssignedIds] = useState((member?.studio_assigned_customers || []).map((a) => `${a.type}:${a.id}`));
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [custLoading, setCustLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/studio/admin/customers');
        setCustomers(r.data.customers || []);
      } catch { /* ignore */ }
      finally { setCustLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const toggle = (key) => {
    setAssignedIds((p) => p.includes(key) ? p.filter((x) => x !== key) : [...p, key]);
  };

  const filtered = customers.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) || (c.type || '').includes(s);
  });

  const save = async () => {
    if (!isEdit && !email.trim()) {
      toast.error('Email required'); return;
    }
    setSaving(true);
    try {
      const assigned_customers = assignedIds.map((k) => {
        const [t, id] = k.split(':');
        return { type: t, id };
      });
      if (isEdit) {
        await api.patch(`/studio/admin/team/${member.id}`, {
          full_name: fullName,
          ...(myRole === 'owner' ? { studio_role: role } : {}),
          assigned_customers,
        });
        toast.success('Updated');
      } else {
        await api.post('/studio/admin/invitations', {
          email: email.trim().toLowerCase(),
          full_name: fullName,
          studio_role: role,
          assigned_customers,
        });
        toast.success(`Invitation email sent to ${email}`);
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur z-50 flex items-center justify-center p-4" data-testid="studio-admin-team-modal">
      <div className="bg-[var(--s-bg)] border border-[var(--s-border)] rounded-xl p-6 w-[640px] max-w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-[var(--s-text)] text-lg">{isEdit ? `Edit ${member.full_name || member.email}` : 'Invite Studio teammate'}</h3>
            {!isEdit && <p className="text-[11px] text-[var(--s-text-dim)] mt-0.5">We'll email them a one-time link to set their own password.</p>}
          </div>
          <button onClick={onClose} className="text-[var(--s-text-dim)] hover:text-[var(--s-text)]"><X size={18} weight="bold" /></button>
        </div>
        <div className="space-y-4">
          {!isEdit && (
            <div>
              <label className="block text-[11px] uppercase tracking-wider font-bold text-[var(--s-text-dim)] mb-1.5">Work email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@founderboard.io" data-testid="studio-admin-team-email" className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-lg px-3 py-2 text-sm text-[var(--s-text)] focus:outline-none focus:border-[var(--s-border-strong)] placeholder:text-[var(--s-text-faint)]" />
            </div>
          )}
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-[var(--s-text-dim)] mb-1.5">Full name <span className="text-[var(--s-text-faint)] normal-case font-normal">(optional — they can change it)</span></label>
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name" data-testid="studio-admin-team-name" className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-lg px-3 py-2 text-sm text-[var(--s-text)] focus:outline-none focus:border-[var(--s-border-strong)] placeholder:text-[var(--s-text-faint)]" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-[var(--s-text-dim)] mb-1.5">Role</label>
            <div className="flex gap-2">
              {['member', ...(myRole === 'owner' ? ['admin'] : [])].map((r) => (
                <button key={r} type="button" onClick={() => setRole(r)} data-testid={`studio-admin-team-role-${r}`} className={`flex-1 px-3 py-2 text-xs font-semibold rounded-md border transition-all ${role === r ? 'text-[var(--s-text)]' : 'text-[var(--s-text-muted)] border-[var(--s-border)] hover:border-[var(--s-border-strong)]'}`} style={role === r ? { backgroundColor: `${ACCENT}26`, borderColor: ACCENT, color: ACCENT } : {}}>
                  {r === 'admin' ? <ShieldCheck size={11} weight="fill" className="inline mr-1" /> : <UserCircle size={11} weight="fill" className="inline mr-1" />}
                  {r}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[var(--s-text-dim)] mt-1">
              {role === 'admin' ? "Admins see every customer + can invite more members." : "Members see only the customers you assign below."}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-[var(--s-text-dim)] mb-1.5">Assigned customers ({assignedIds.length})</label>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search to filter…" className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-lg px-3 py-2 text-xs text-[var(--s-text)] placeholder:text-[var(--s-text-faint)] mb-2 focus:outline-none focus:border-[var(--s-border-strong)]" />
            <div className="max-h-[260px] overflow-y-auto border border-[var(--s-border)] rounded-lg divide-y divide-[var(--s-border)]" data-testid="studio-admin-team-customer-picker">
              {custLoading ? (
                <div className="text-sm text-[var(--s-text-dim)] p-4">Loading customers…</div>
              ) : filtered.length === 0 ? (
                <div className="text-sm text-[var(--s-text-dim)] p-4">No customers match.</div>
              ) : filtered.map((c) => {
                const key = `${c.type}:${c.id}`;
                const checked = assignedIds.includes(key);
                return (
                  <label key={key} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[var(--s-surface-hover)]" data-testid={`studio-admin-team-cust-${c.id}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(key)} className="accent-[#6366f1]" />
                    <span className="text-[10px] uppercase tracking-wider font-bold flex-shrink-0" style={{ color: ACCENT }}>{c.type}</span>
                    <span className="text-sm text-[var(--s-text)] truncate flex-1">{c.name || '—'}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-[var(--s-border)] rounded-lg text-[var(--s-text-muted)] hover:text-[var(--s-text)]">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="studio-admin-team-save" className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: ACCENT }}>
            {saving ? <Spinner size={14} className="animate-spin" /> : <Check size={14} weight="bold" />}
            {isEdit ? 'Save changes' : 'Send invitation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudioAdmin;
